import { z } from 'zod';

/**
 * GATED — первый шаг и открытые хабы. FULL — все разделы; так становится
 * только после явного unlock через ассистента, не из-за возраста аккаунта.
 */
export const MeAccessMode = z.enum(['FULL', 'GATED']);
export type MeAccessMode = z.infer<typeof MeAccessMode>;

/** Пункты нижнего меню mini-app, которые можно открывать по одному. */
export const NavHub = z.enum(['events', 'people', 'chats', 'more']);
export type NavHub = z.infer<typeof NavHub>;

export const UnlockProgressHub = z.enum(['events', 'people', 'chats', 'more', 'all']);
export type UnlockProgressHub = z.infer<typeof UnlockProgressHub>;

export const UnlockProgressInput = z.object({
  hub: UnlockProgressHub,
});
export type UnlockProgressInput = z.infer<typeof UnlockProgressInput>;

/**
 * Тип якоря входа. `person` = визитка user/organizer.
 * `game` entityId — слаг игры (`journey` / `leela` / `mission`), не uuid.
 */
export const AnchorType = z.enum(['event', 'service', 'person', 'game', 'place', 'news', 'club']);
export type AnchorType = z.infer<typeof AnchorType>;

export const AnchorStatus = z.enum(['open', 'archived']);
export type AnchorStatus = z.infer<typeof AnchorStatus>;

export const AnchorDto = z.object({
  id: z.uuid(),
  type: AnchorType,
  entityId: z.string().min(1),
  addedAt: z.iso.datetime(),
  status: AnchorStatus,
});
export type AnchorDto = z.infer<typeof AnchorDto>;

export const ActiveStepType = z.enum([
  'event_rsvp',
  'service_contact',
  'person_meet',
  'game_play',
  'game_hold',
  'city_fallback',
  'intent_ask',
]);
export type ActiveStepType = z.infer<typeof ActiveStepType>;

export const ActiveStepDto = z.object({
  type: ActiveStepType,
  /** Объект шага. У `city_fallback` / `intent_ask` может не быть. */
  entityId: z.string().min(1).optional(),
  /** Машинный ключ CTA — копирайт в view-logic, не в API. */
  ctaKey: z.string().min(1),
  /** Реплика ассистента уже персональная — её пишет сервер. */
  assistantLine: z.string().min(1),
});
export type ActiveStepDto = z.infer<typeof ActiveStepDto>;

/**
 * Состояние социального цикла по фокусной паре.
 * `none` — граф пуст, пересечений нет (фоллбэк, не встреча).
 */
export const CycleState = z.enum([
  'none',
  'interest',
  'suggested',
  'meeting_created',
  'meeting_happened',
  'feedback',
]);
export type CycleState = z.infer<typeof CycleState>;

export const GameStatus = z.enum(['playable', 'done', 'unavailable']);
export type GameStatus = z.infer<typeof GameStatus>;

/**
 * Сырьё для `evaluateGameStatus`: флаг фичи + есть ли сессия + фаза экрана.
 * `phase` совпадает с `journeyPhase` (и тем же набором для других игр).
 */
export const GameSessionDto = z.object({
  featureEnabled: z.boolean(),
  entityExists: z.boolean(),
  phase: z.enum(['intro', 'drafting', 'active', 'plan-done', 'finished', 'halted']),
});
export type GameSessionDto = z.infer<typeof GameSessionDto>;

export const MeProgressDto = z.object({
  mode: MeAccessMode,
  /** Вершина стека — последний `open`. Холодный заход стек не трогает. */
  anchors: z.array(AnchorDto),
  activeStep: ActiveStepDto,
  cycleState: CycleState,
  /** В GATED: какие вкладки уже в меню. В FULL клиент поле игнорирует. */
  unlockedNav: z.array(NavHub),
  /** Только если фокус — игра. Иначе поле опущено. */
  gameStatus: GameStatus.optional(),
});
export type MeProgressDto = z.infer<typeof MeProgressDto>;
